import React, {useCallback, useEffect, useRef, useState} from 'react';

/* Styling & Icons */
import {style, stylesheet} from 'typestyle';
import EditIcon from '@mui/icons-material/Edit';
import InstagramIcon from '@mui/icons-material/Instagram';
import Chip from '@mui/material/Chip';
import {CircularProgress, createTheme, ThemeProvider} from '@mui/material';
import {ADALONG_COLORS} from '@src/styles';

/* Misc */
import {EPopup} from '@src/types/common';
import { LocalizedStrings } from '@src/utils/localizedStrings';

/* Api */
/* Components */
import {PopupOverlay} from '@src/components/PopupOverlay';
import {Loader} from '@src/components/Loader';
import {PerfectScrollbar} from 'components/PerfectScrollbar';
import {ActionButton} from '@src/components/ActionButton';
import {Hide} from '@src/components/Hide';
import {FormError} from '@src/components/Form/FormError';

/* Types */
import {FacebookPage, CreatorView} from 'common';
import {Services} from '@src/types/Services';
import {SocialNetworkButton, SocialNetworkType} from '../../../../components/SocialNetworkButton';
import {useQueryInfluencersCreators, useQueryBlacklistedCreators} from '../../../../queries/influencers/useQueryCreators';
import {useQueryUserConfig} from '../../../../queries/users/useQueryUserConfig';
import {useQueryFacebookPages} from '../../../../queries/users/useQueryFacebookPages';
import {useQueryGroupMentionPages} from '../../../../queries/users/useQueryGroupMentionPages';
import {api} from '../../../../services/api';
import * as appHelpers from '@src/services/helpers/appHelpers';
import { setPopup } from '@src/state/popupAtoms';
import {queryClient, QUERY_KEYS} from '../../../../query';
import {useWindowFocus} from '@src/hooks/useWindowFocus';

interface Props {
  services: Services;
}

const materialTheme = createTheme({
  palette: {
    primary: {
      main: ADALONG_COLORS.LIGHT_GRAY,
    },
  },
} as any);

export function Settings(props: Props) {
  const { data: userConfig, refetch: refetchUserConfig } = useQueryUserConfig();
  const { data: facebookPages = [], refetch: refetchFacebookPages, isLoading: facebookPagesLoading } = useQueryFacebookPages();
  const { data: groupMentionPages = [], refetch: refetchGroupMentionPages } = useQueryGroupMentionPages();
  const user = userConfig?.user ?? null;
  const oauth = user?.oauth;
  const focus = useWindowFocus();
  const [mounted, setMounted] = useState(false);
  const [mentionFb, setMentionFb] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);
  const [selectingPageId, setSelectingPageId] = useState('');
  const [activatingPageId, setActivatingPageId] = useState('');
  useEffect(() => {
    setMentionFb(!!(oauth?.facebook) && !isEmpty(oauth?.facebook));
  }, [oauth?.facebook])

  const isEmpty = (obj: Object) => Object.keys(obj).length === 0;

  const { data: influencers = [], isLoading, error: errorLoading } = useQueryInfluencersCreators();
  const { data: blacklist = [], isLoading: blacklistedLoading, error: blacklistedError } = useQueryBlacklistedCreators();

  const whitelist = influencers.filter((inf) => !inf.isBlacklisted);

  const getCreatorCountries = (creator: CreatorView): string => {
    return creator.countries ? creator.countries.join(', ') : '';
  };

  const getInfluencerStatus = (influencer: CreatorView) => {
    if (influencer.isInfluencerDisabled) {
      return 'inactive';
    }
    return 'active';
  };

  const getInfluencerStatusVariant = (influencer: CreatorView) => {
    if (influencer.isInfluencerDisabled) {
      return 'outlined';
    }
    return undefined;
  };

  const influencersRef = useRef(null);

  const blacklistRef = useRef(null)

  const openAddInfluencer = () => {
    setPopup(EPopup.addInfluencer, true);
  };

  const openAddBlacklist = () => {
    setPopup(EPopup.addBlacklist, true);
  }

  const openEditInfluencer = (influencer: CreatorView) => {
    setPopup(EPopup.editInfluencer, influencer);
  };

  /* instagram pages */
  const instagramPages = (facebookPages || [])
    .filter((p) => p.instagram)
    .map((p) => ({
      page: p.instagram!,
      fbToken: p.token,
    }));
  const pages = useRef(null);
  const activatedPageIds = new Set(groupMentionPages.map((page) => page.id));

  const getMentionPagePayload = (page: FacebookPage, fbPageToken: string) => ({
    id: page.id,
    token: page.token,
    name: page.name,
    fbPageToken,
    source: 'instagram' as const,
  });

  const onSelectPage = (page: FacebookPage, fbPageToken: string) => {
    if (!page || selectingPageId || activatingPageId) {
      return;
    }
    setSelectingPageId(page.id);
    api
      .setMentionPage(getMentionPagePayload(page, fbPageToken))
      .then(
        () => {
          setSelectingPageId('');
          refetchUserConfig();
          queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userGroupMentionPages() });
        },
        (err) => {
          console.error(err);
          setSelectingPageId('');
          appHelpers.addAlert({
            type: 'danger',
            text: window.T('common.settings.mention.error_set'),
          });
        }
      );
  };

  const onActivatePage = (page: FacebookPage, fbPageToken: string) => {
    if (!page || selectingPageId || activatingPageId || activatedPageIds.has(page.id)) {
      return;
    }

    setActivatingPageId(page.id);
    api
      .activateMentionPage(getMentionPagePayload(page, fbPageToken))
      .then(
        async () => {
          setActivatingPageId('');
          await Promise.all([
            refetchGroupMentionPages(),
            queryClient.invalidateQueries({ queryKey: QUERY_KEYS.userGroupMentionPages() }),
          ]);
        },
        (err) => {
          console.error(err);
          setActivatingPageId('');
          appHelpers.addAlert({
            type: 'danger',
            text: window.T('common.settings.mention.error_activate'),
          });
        },
      );
  };

  /* general functions */
  const refresh = useCallback((): void | NodeJS.Timeout => {
    const loop = () => setTimeout(
      () => setRefreshCount(refreshCount + 1),
      3000,
    )
    if (!focus) {
      return loop();
    }

    const facebookSigned = user?.oauth?.facebook;
    refetchUserConfig().then(({ data: newUserConfig }) => {
      if (!facebookSigned && newUserConfig?.user?.oauth?.facebook) {
        refetchFacebookPages();
      }
      loop();
    });
  }, [refreshCount, user]);

  useEffect(() => {
    if (!mounted) {
      return;
    }
    refresh();
  }, [refreshCount])

  const close = useCallback(() => {
    setPopup(EPopup.settings, false);
  }, []);

  useEffect(() => {
    setMounted(true);
    refresh();
    if (user?.oauth?.facebook) {
      refetchFacebookPages();
    }
    return () => {
      setMounted(false);
    };
  }, []);

  if (!user) {
    return null; // TODO: loading
  }

  const removeOauth = (social: SocialNetworkType) => new Promise<void>((resolve, reject) => {
    api.removeOauth(social, (err) => {
      if (err) return reject(err);
      refetchUserConfig();
      resolve();
    });
  });

  return (
    <ThemeProvider theme={materialTheme}>
      <PopupOverlay show={true} onClose={close} title={<span className={titleTextClass}>{lang.title}</span>} width="min(800px, 92vw)" cardClassName={cardHeightClass} zIndex={3000}>
        <PerfectScrollbar className={contentClass}>
            <section id='oauth'>
              <div>
                <div className={sectionTitleClass}>{window.T('common.settings.oauth.title')}</div>
                <div className={sectionDescClass}>{window.T('common.settings.oauth.desc')}</div>
              </div>
              <div className={oauthButtonsClass}>
                {(['facebook'] as const).map((social) => (
                  <SocialNetworkButton
                    social={social}
                    key={social}
                    linked={!!(oauth?.[social]) && !isEmpty(oauth?.[social])}
                    onRemove={(social) => removeOauth(social)}
                  />
                ))}
              </div>
            </section>
            <section id='mention' className={sectionClass}>
              <div>
                <div className={sectionTitleClass}>{window.T('common.settings.mention.title')}</div>
                <div className={sectionDescClass}>
                  {window.T(`common.settings.mention.${mentionFb ? 'desc' : 'desc_link'}`)}
                </div>
              </div>
              {mentionFb ? (
                facebookPagesLoading ? (
                  <div className={sheet.loader}>
                    <Loader size={24} />
                  </div>
                ) : (
                <PerfectScrollbar
                  className={pagesClass}
                  ref={pages}
                  data-emptytext={
                    instagramPages.length ? false : window.T('common.settings.mention.empty')
                  }
                >
                  {instagramPages.map(({ page, fbToken }, i) => {
                    const mention = user?.mention || {};
                    const selected = page.id === mention.instagram?.id;
                    const activated = selected || activatedPageIds.has(page.id);
                    const selecting = selectingPageId === page.id;
                    const activating = activatingPageId === page.id;
                    const busy = !!selectingPageId || !!activatingPageId;
                    return (
                      <div
                        className={pageRowClass}
                        key={i}
                        title={page.name}
                        onClick={() => onSelectPage(page, fbToken)}
                        data-selected={selected}
                        data-disabled={busy}
                      >
                        <span>
                          <InstagramIcon className={instaIconClass} />
                          {page.name}
                        </span>
                        <div className={pageSelectorsClass} data-selecting={busy}>
                          {!selected ? null : (
                            <Chip
                              label='main'
                              size='small'
                              color='primary'
                              sx={{ r: 5, backgroundColor: ADALONG_GREEN, color: ADALONG_COLORS.DARK_BLACK, fontWeight: 700 }}
                            />
                          )}
                          <button
                            type='button'
                            className={pageStatusButtonClass}
                            title={activated ? 'Activated' : 'Activate page'}
                            aria-label={activated ? 'Page activated' : 'Activate page'}
                            data-activated={activated}
                            disabled={activated || busy}
                            onClick={(e) => {
                              e.stopPropagation();
                              onActivatePage(page, fbToken);
                            }}
                          >
                            <span className={pageStatusDotClass} />
                          </button>
                        </div>
                        {!(selecting || activating) ? null : (
                            <div className={loadingClass}>
                            <Loader size={12} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </PerfectScrollbar>
                )
              ) : null }
            </section>

            <section id='influencers' className={sectionClass}>
              <div>
                <div className={sectionTitleClass}>{lang.influencerTitle}</div>
                <div className={sectionDescClass}>{lang.influencerDesc}</div>
              </div>

              {whitelist.length > 0 ? (
                <PerfectScrollbar className={influencerListClass} ref={influencersRef}>
                  {isLoading ? (
                    <CircularProgress color='inherit' />
                  ) : (
                    whitelist.map((influencer) => (
                      <div className={influencerRowClass} key={influencer._id} title={influencer.username}>
                        <span className={influencerInfoClass}>
                          <InstagramIcon className={instaIconClass} />
                          {influencer.username}
                        </span>
                        <div className={influencerInfoClass}>
                          {getCreatorCountries(influencer)}
                        </div>
                        <div>
                          <Chip
                            label={getInfluencerStatus(influencer)}
                            variant={getInfluencerStatusVariant(influencer)}
                            size='small'
                            color='primary'
                            sx={{ r: 5, ...(!influencer.isInfluencerDisabled && { backgroundColor: ADALONG_COLORS.ADALONG_YELLOW, color: ADALONG_COLORS.DARK_BLACK, fontWeight: 700 }) }}
                          />
                          <EditIcon
                            onClick={() => openEditInfluencer(influencer)}
                            className={editClass}
                          />
                        </div>
                      </div>
                    ))
                  )}
                </PerfectScrollbar>
              ) : null }

              <Hide hide={!errorLoading}>
                <FormError error={lang.error.refresh} />
              </Hide>

              <div className={sheet.button}>
                <ActionButton variant='blue' onClick={() => openAddInfluencer()} size='medium'>
                  {lang.button.toUpperCase()}
                </ActionButton>
              </div>
            </section>

            <section id='blacklist' className={sectionClass}>
              <div>
                <div className={sectionTitleClass}>{lang.blacklistTitle}</div>
              </div>

              {blacklist.length > 0 ? (
                  <PerfectScrollbar className={influencerListClass} ref={blacklistRef}>
                    {blacklistedLoading ? (
                        <CircularProgress color='inherit' />
                    ) : (
                        blacklist.map((creator) => (
                            <div className={influencerRowClass} key={creator._id} title={creator.username}>
                        <span className={influencerInfoClass}>
                          <InstagramIcon className={instaIconClass} />
                          {creator.username}
                        </span>
                              <div className={influencerInfoClass}>
                                {getCreatorCountries(creator)}
                              </div>
                              <div>
                                <Chip
                                    label={'blacklisted'}
                                    variant={'filled'}
                                    size='small'
                                    color='primary'
                                    sx={{ r: 5 }}
                                />
                                <EditIcon
                                    onClick={() => openEditInfluencer(creator)}
                                    className={editClass}
                                />
                              </div>
                            </div>
                        ))
                    )}
                  </PerfectScrollbar>
              ) : null }

              <Hide hide={!errorLoading && !blacklistedError}>
                <FormError error={lang.error.refresh} />
              </Hide>

              <div className={sheet.button}>
                <ActionButton variant='blue' onClick={() => openAddBlacklist()} size='medium'>
                  {lang.blackListButton.toUpperCase()}
                </ActionButton>
              </div>
            </section>

        </PerfectScrollbar>
      </PopupOverlay>
    </ThemeProvider>
  );
}

const lang = LocalizedStrings({
  en: {
    title: 'Organization Settings',
    blacklistTitle: 'Blacklisted creators',
    influencerTitle: 'Instagram Influencer Whitelist',
    influencerDesc:
      "Add all your instagram influencer's usernames in this section to automatically set the rights agreed on their content, as well as to access special filter options.",
    button: 'Add Influencer',
    blackListButton: 'Blacklist a creator',
    cancel: 'Cancel',
    error: {
      refresh: 'Unable to get influencer list. Please try again or contact us.',
      unknown: 'An error occurred while removing the influencer, please try again or contact us.',
    },
  },
});

const { FLASH_BLUE, ADALONG_YELLOW, ADALONG_GREEN } = ADALONG_COLORS;

const titleTextClass = style({
  fontSize: 26,
  margin: '10px 0 15px' 
});

const instaIconClass = style({
  fontSize: '20px !important',
  marginRight: 5,
  verticalAlign: 'middle',
});

const contentClass = style({
  flex: 1,
  minHeight: 0,
  paddingRight: 8,
});

const cardHeightClass = style({
  maxHeight: '85vh',
});

const sectionTitleClass = style({
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: ADALONG_COLORS.SMOOTH_WHITE,
  borderLeft: `3px solid ${FLASH_BLUE}`,
  paddingLeft: 10,
  marginBottom: 6,
});

const sectionDescClass = style({
  fontSize: 13,
  lineHeight: '1.5',
  paddingLeft: 13,
  marginBottom: 16,
  color: ADALONG_COLORS.LIGHT_GRAY,
});

const sectionClass = style({
  marginBottom: 8,
  paddingTop: 20,
  borderTop: `1px solid rgba(255,255,255,0.06)`,
});

const oauthButtonsClass = style({
  marginTop: 16,
  marginBottom: 8,
  paddingLeft: 13,
});

const listContainerClass = style({
  position: 'relative',
  overflow: 'hidden',
  border: `1px solid rgba(255,255,255,0.08)`,
  backgroundColor: ADALONG_COLORS.DARK_BLACK,
  borderRadius: 10,
  maxHeight: 200,
  marginBottom: 12,
});

const pagesClass = listContainerClass;

const pageRowClass = style({
  cursor: 'pointer',
  color: ADALONG_COLORS.LIGHT_GRAY,
  padding: '8px 16px',
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  transition: 'background-color 0.15s',
  $nest: {
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
    '&[data-selected="true"]': { backgroundColor: 'rgba(191,253,223,0.12)' },
    '&[data-disabled="true"]': { cursor: 'progress' },
    '&[data-selected="true"] > span': { color: ADALONG_COLORS.SMOOTH_WHITE },
    '> span': { display: 'inline-flex', alignItems: 'center', maxWidth: 400 },
  },
});

const pageSelectorsClass = style({
  position: 'relative',
  marginRight: 2,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  $nest: {
    '&[data-selecting="true"]': { pointerEvents: 'none', filter: 'contrast(14%) blur(0.5px)' },
  },
});

const pageStatusButtonClass = style({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 22,
  height: 22,
  border: 0,
  padding: 0,
  background: 'transparent',
  cursor: 'pointer',
  $nest: {
    '&:disabled': { cursor: 'default' },
  },
});

const pageStatusDotClass = style({
  width: 12,
  height: 12,
  borderRadius: '50%',
  display: 'inline-block',
  backgroundColor: '#ff5e5e',
  boxShadow: '0 0 0 2px rgba(255,94,94,0.15)',
  transition: 'transform 0.15s, box-shadow 0.15s, background-color 0.15s',
  $nest: {
    [`.${pageStatusButtonClass}:hover &`]: {
      transform: 'scale(1.08)',
      boxShadow: '0 0 0 4px rgba(255,94,94,0.12)',
    },
    [`.${pageStatusButtonClass}[data-activated="true"] &`]: {
      backgroundColor: ADALONG_GREEN,
      boxShadow: '0 0 0 2px rgba(191,253,223,0.15)',
    },
    [`.${pageStatusButtonClass}[data-activated="true"]:hover &`]: {
      transform: 'none',
      boxShadow: '0 0 0 2px rgba(191,253,223,0.15)',
    },
  },
});

const influencerListClass = listContainerClass;

const influencerRowClass = style({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  color: ADALONG_COLORS.LIGHT_GRAY,
  padding: '8px 16px',
  transition: 'background-color 0.15s',
  $nest: {
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
  },
});

const influencerInfoClass = style({
  width: '35%',
  display: 'inline-flex',
  alignItems: 'center',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  color: ADALONG_COLORS.SMOOTH_WHITE,
});

const loadingClass = style({
  display: 'flex',
  alignItems: 'center',
  marginLeft: 8,
});

const editClass = style({
  paddingLeft: 5,
  cursor: 'pointer',
  color: ADALONG_COLORS.LIGHT_GRAY,
  transition: 'color 0.2s',
  verticalAlign: 'middle',
  fontSize: '20px !important',
  $nest: {
    '&:hover': { color: ADALONG_COLORS.SMOOTH_WHITE },
  },
});

const sheet = stylesheet({
  button: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: 8,
  },
  loader: {
    display: 'flex',
    justifyContent: 'center',
    padding: '16px 0',
  },
});

export default Settings;
